import { AxiosError } from 'axios';
import type { RouterNavigateOptions, To } from 'react-router-dom';
import { createBrowserRouter, matchRoutes } from 'react-router-dom';

import { getIsLogin } from '@/features/auth/authStore';
import { initCacheRoutes, routes } from '@/router';
import { store } from '@/store';

import { initAuthRoutes } from './initRouter';
import { type LocationQueryRaw, stringifyQuery } from './query';
import { isAlreadyPatch, setCacheRoutes, setIsAlreadyPatch } from './routeStore';

async function initRouter() {
  store.dispatch(setIsAlreadyPatch(false));

  function getIsNeedPatch(path: string) {
    if (!getIsLogin(store.getState())) return false;

    if (isAlreadyPatch()) return false;

    const matchRoute = matchRoutes(routes, { pathname: path }, import.meta.env.VITE_BASE_URL);

    if (!matchRoute) return true;

    if (matchRoute) {
      return matchRoute[1].route.path === '*';
    }

    return false;
  }

  const reactRouter = createBrowserRouter(routes, {
    basename: import.meta.env.VITE_BASE_URL,
    patchRoutesOnNavigation: async ({ patch, path }) => {
      try {
        if (getIsNeedPatch(path)) {
          store.dispatch(setIsAlreadyPatch(true));
          await initAuthRoutes(patch);
        }
      } catch (err) {
        console.log(err);
        const modalLogoutCodes = import.meta.env.VITE_SERVICE_MODAL_LOGOUT_CODES?.split(',') || [];
        if (err instanceof AxiosError && modalLogoutCodes.includes(String(err?.response?.data?.code))) {
          // handleLogout();
          reactRouter.navigate('/login-out');
        }
      }
    }
  });

  store.dispatch(setCacheRoutes(initCacheRoutes));

  if (getIsLogin(store.getState()) && !isAlreadyPatch()) {
    initAuthRoutes(reactRouter.patchRoutes);

    store.dispatch(setIsAlreadyPatch(true));
  }

  function resetRoutes() {
    store.dispatch(setIsAlreadyPatch(false));
    reactRouter._internalSetRoutes(routes);
  }

  return {
    reactRouter,
    resetRoutes
  };
}

async function navigator() {
  const { reactRouter, resetRoutes } = await initRouter();

  async function navigate(path: To | null, options?: RouterNavigateOptions) {
    reactRouter.navigate(path, options);
  }

  function back() {
    reactRouter.navigate(-1);
  }

  function forward() {
    reactRouter.navigate(1);
  }

  function go(delta: number) {
    reactRouter.navigate(delta);
  }

  function replace(path: To) {
    reactRouter.navigate(path, { replace: true });
  }

  function reload() {
    reactRouter.navigate(0);
  }

  function navigateUp() {
    reactRouter.navigate('..');
  }

  function goHome() {
    reactRouter.navigate('/');
  }

  // eslint-disable-next-line max-params
  function push(path: string, query?: LocationQueryRaw, state?: any, _replace?: boolean) {
    let _path = path;

    if (query) {
      const search = stringifyQuery(query);

      _path = `${path}?${search}`;
    }

    reactRouter.navigate(_path, { replace: _replace, state });
  }

  return {
    back,
    forward,
    go,
    goHome,
    navigate,
    navigateUp,
    push,
    reactRouter,
    reload,
    replace,
    resetRoutes
  };
}

export const router = await navigator();

export type RouterContextType = Awaited<ReturnType<typeof navigator>>;
